import { HelpFeature } from '@/model/HelpModel';
import { Spin } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { batch } from 'react-redux';
import { AddEditModal, ListView, Toolbar } from '..';
import { handleResponse } from '../../../Helper';
import { velocityVisionApi } from '../../../api';
import { User } from '../../../model/AccountModel';
import { PaginationSetting, SortDirections, ViewType } from '../../../model/CommonModel';
import { Instance } from '../../../model/VelocityVisionModel';
import { HeaderTitleBar, HelpFactoryComponent, VelocityVisionIcon } from '../../common';
import { GridDataProps, GridListView } from '../../common/GridListView/GridListView';
import styles from './velocityVision.module.scss';

const defaultPagination: PaginationSetting = {
	PageNumber: 1,
	PageSize: 25,
	SearchedValue: '',
	SortDirection: SortDirections.None,
	SortField: '',
	TotalItems: 0,
};
const topicId: string = 'VelocityVisionTab';

const VelocityVision: React.FC = () => {
	const [loading, setLoading] = useState<boolean>(true);
	const [view, setView] = useState<ViewType>(ViewType.List);
	const [paginationSetting, setPaginationSetting] = useState<PaginationSetting>(defaultPagination);
	const [data, setData] = useState<Instance[]>([] as Instance[]);
	const [editInstance, setEditInstance] = useState<Instance>(undefined);
	const [showAddEditModal, setShowAddEditModal] = useState<boolean>(false);
	const [gridData, setGridData] = useState<GridDataProps<{ instance: string; onClick: () => void }>[]>([]);
	const tabsRef = useRef<{ [key: string]: Window | null }>({});

	const user: User = getUser();

	useEffect(() => {
		loadData(paginationSetting).finally(() => {
			setLoading(false);
		});
	}, []);

	useEffect(() => {
		if (view === ViewType.Card) {
			gridDataLoad();
		}
	}, [view, JSON.stringify(data)]);

	const loadData = async (pagination: PaginationSetting): Promise<void> => {
		const response = await velocityVisionApi.getInstances(pagination);
		if (!handleResponse(response)) {
			const {
				Entity: { Instances: dataReturned, TotalItemsPaginated },
			} = response;
			batch(() => {
				setData(dataReturned);
				setPaginationSetting({ ...pagination, TotalItems: TotalItemsPaginated });
			});
		}
	};

	const tabInstanceOpenOrFocus = (url: string, name: string) => {
		const tabs = tabsRef.current;
		if (!tabs[url] || tabs[url]?.closed) {
			const newTab = window.open(url, name);
			if (newTab) {
				tabs[url] = newTab;
			}
		} else {
			tabs[url]?.focus();
		}
	};

	const gridDataLoad = () => {
		const gridData: GridDataProps<{ instance: string; onClick: () => void }>[] = data.map(item => {
			return {
				title: item.Name,
				id: item.Id,
				extraData: {
					instance: item.InstanceUrl,
					onClick: () => {
						tabInstanceOpenOrFocus(item.InstanceUrl, item.Name);
					},
				},
			};
		});
		setGridData(gridData);
	};

	const onDeleteInstance = async (id: number): Promise<boolean> => {
		if (user.isAdmin) {
			const response = await velocityVisionApi.deleteInstances([id]);
			if (!handleResponse(response)) {
				loadData(paginationSetting);
				return true;
			}

			return false;
		}
	};

	const onAddNewOrEditInstanceButtonClick = (visionItemId: number = 0) => {
		if (user.isAdmin) {
			let instance: Instance;
			if (visionItemId !== 0) instance = data.find(item => item.Id === visionItemId);
			batch(() => {
				setEditInstance(instance);
				setShowAddEditModal(true);
			});
		}
	};

	const onAddEditModalConfirmation = () => {
		setShowAddEditModal(false);
		setLoading(true);
		loadData(paginationSetting).finally(() => {
			setLoading(false);
		});
	};

	const onAddEditModalCancel = () => {
		setShowAddEditModal(false);
	};

	const createAddEditModal = () => {
		if (!showAddEditModal) return;
		const title: string = editInstance ? _('EditInstance') : _('AddInstance');
		return <AddEditModal editInstance={editInstance} title={title} onConfirm={onAddEditModalConfirmation} onCancel={onAddEditModalCancel} />;
	};

	return (
		<Spin tip={`${_('Loading')}...`} spinning={loading} size='large'>
			<HeaderTitleBar
				id='visionContainer'
				title={_('VelocityVision')}
				icon={<VelocityVisionIcon />}
				helpButton={<HelpFactoryComponent helpFeature={HelpFeature.VelocityVision} topicId={topicId} isLight size='medium' />}
			/>
			<div className={styles.velocityContainer}>
				<Toolbar
					selectedView={view}
					isUserAdmin={user.isAdmin}
					onViewChangedHandled={setView}
					onAddNewButtonClick={onAddNewOrEditInstanceButtonClick}
				/>
				{view === ViewType.List ? (
					<ListView
						data={data}
						isUserAdmin={user.isAdmin}
						loadData={loadData}
						paginationSetting={paginationSetting}
						deleteInstances={onDeleteInstance}
						onAddNewButtonClick={onAddNewOrEditInstanceButtonClick}
						onTryOpenOrFocusTab={tabInstanceOpenOrFocus}
					/>
				) : (
					<GridListView
						data={gridData}
						isUserAdmin={user.isAdmin}
						actions={{ onEdit: onAddNewOrEditInstanceButtonClick, onDelete: onDeleteInstance }}
						defaultIcon={<VelocityVisionIcon style={{ color: 'red', width: 110, height: 110 }} />}
						canDelete={true}
						canEdit={true}
					/>
				)}
				{createAddEditModal()}
			</div>
		</Spin>
	);
};

export { VelocityVision };
